"use client";

import { prefetchRecommendations } from "@pamperedchef/consumer-components/product";
import { useEffect } from "react";

import {
	addToCart,
	type AddToCartActionResponse,
	type AddToCartActionStatus
} from "@/app/actions/cart";
import { getProductsBySkus } from "@/app/actions/product";
import {
	PC_ADD_TO_CART_EVENT,
	PC_ADD_TO_CART_RESULT_EVENT,
	isAddToCartEventDetail,
	type AddToCartEventDetail,
	type AddToCartResultDetail
} from "@/app/components/shared/recommended-products/add-to-cart-event";
import {
	useCartStore,
	useSessionStore,
	useAddToCartSliderStore,
	useAddToCartErrorStore
} from "@/app/store";
import { getAddToCartErrorMessage } from "@/app/hooks/useAddToCart/errorMessages";
import { analyticsManager } from "@/app/utilities/analytics";

/**
 * The "event door" into add-to-cart, for surfaces that can't call React
 * directly — external bundles like `consumer-web-elements` (Shadow-DOM web
 * components). They dispatch a `pc:add-to-cart` window event; this single,
 * always-mounted listener runs the SAME canonical `addToCart` action, cart
 * store, and slider that the native `useAddToCart` hook uses. It is not a
 * separate cart — just the bridge for non-React callers.
 *
 * Native React surfaces (PDP button, in-tree composites) skip this and call
 * `useAddToCart` directly. Mounted once in the root layout.
 */
export function CartEventBridge(): null {
	const openSlider = useAddToCartSliderStore((state) => state.open);
	const closeSlider = useAddToCartSliderStore((state) => state.close);
	const setCartState = useCartStore((state) => state.setCartState);

	useEffect(() => {
		function reportResult(
			detail: AddToCartEventDetail,
			status: AddToCartResultDetail["status"]
		): void {
			if (!detail.requestId) return;
			window.dispatchEvent(
				new CustomEvent<AddToCartResultDetail>(
					PC_ADD_TO_CART_RESULT_EVENT,
					{ detail: { requestId: detail.requestId, status } }
				)
			);
		}

		function reportFailure(
			detail: AddToCartEventDetail,
			status: AddToCartActionStatus
		): void {
			closeSlider();
			useAddToCartErrorStore
				.getState()
				.fail(status, getAddToCartErrorMessage(status));
			reportResult(detail, "error");
		}

		async function handleAddToCart(event: Event): Promise<void> {
			const detail = (event as CustomEvent).detail;
			if (!isAddToCartEventDetail(detail)) return;

			const sessionState = useSessionStore.getState().sessionState;
			if (!sessionState) {
				reportFailure(detail, "session-expired");
				return;
			}

			// Must start before the cart call so both requests overlap.
			prefetchRecommendations({
				source: "ATC",
				sku: detail.productCode,
				locale: sessionState.locale
			});

			// Cross-sell adds come through here; surface failures in the ribbon.
			// A rejected action (network/server error) throws — mirror the
			// useAddToCart hook and report it as a network error.
			let response: AddToCartActionResponse;
			try {
				response = await addToCart(sessionState, {
					productCode: detail.productCode,
					quantity: detail.quantity,
					purchaseType: detail.purchaseType
				});
			} catch {
				reportFailure(detail, "network-error");
				return;
			}

			if (response.status !== "success") {
				reportFailure(detail, response.status);
				return;
			}

			useAddToCartErrorStore.getState().clear();
			setCartState(response.cart);
			reportResult(detail, "success");

			const result = await getProductsBySkus([detail.productCode]);
			const product =
				result.status === "success" ? result.products[0] : undefined;

			analyticsManager.track({
				type: "add_to_cart",
				sku: detail.productCode,
				quantity: Number(detail.quantity) || 1,
				price: product?.salePrice ?? 0,
				locale: sessionState.locale,
				source: detail.source
			});

			if (!product) return;

			openSlider({
				sku: product.sku,
				quantity: Number(detail.quantity) || 1,
				name: product.name,
				imageUrl: product.image || detail.imageUrl || "",
				listPrice: product.listPrice,
				salePrice: product.salePrice
			});
		}

		window.addEventListener(PC_ADD_TO_CART_EVENT, handleAddToCart);
		return () => {
			window.removeEventListener(PC_ADD_TO_CART_EVENT, handleAddToCart);
		};
	}, [openSlider, closeSlider, setCartState]);

	return null;
}
